/**
 * serviceOrderAsset — o bloco "Equipamento / objeto" da Ordem de Serviço.
 *
 * É o bloco que separa uma OS de assistência técnica de uma OS de instalação:
 * quem recebe um aparelho para consertar precisa registrar O QUE recebeu, com
 * que número de série, com quais acessórios e em que estado — porque na entrega
 * essas quatro coisas são exatamente o que pode virar discussão ("faltou o
 * carregador", "esse risco já estava aí").
 *
 * Gêmeo estrutural de `src/lib/serviceOrderDiagnosis.ts`, e pelos mesmos dois
 * motivos:
 *
 * 1. **Ausência ≠ vazio.** `null` é "esta OS não tem equipamento" (instalação,
 *    limpeza, jardinagem — a maioria); `''` seria "tem e está em branco". A
 *    gravação normaliza string em branco para `null` para o PDF não imprimir um
 *    título de seção sem conteúdo (CLAUDE.md #61).
 *
 * 2. **Escrita E leitura.** `service_orders` é gravada por LISTA EXPLÍCITA de
 *    colunas em seis lugares. Acrescentar coluna só na migration + no formulário
 *    faz o save "dar certo" e o valor nunca chegar ao banco (#33), e perder o
 *    campo na LEITURA faz o próximo save gravar `null` por cima (#70). Por isso
 *    o par builder/leitor abaixo, e o guard de varredura cobrando os dois
 *    sentidos.
 *
 * O ESTADO DE CONSERVAÇÃO é texto livre, não uma escala fechada ("bom/regular/
 * ruim"): o que protege o prestador é a descrição do risco na tampa, não um
 * adjetivo. Uma escala daria a impressão de precisão sem registrar nada.
 */

export interface ServiceOrderAsset {
  /** Tipo do ativo: notebook, ar-condicionado, veículo… */
  assetType: string | null;
  /** Marca e modelo, juntos — é assim que se escreve ("Samsung Galaxy S24"). */
  assetBrand: string | null;
  /** Identificador único: nº de série, placa, IMEI, patrimônio. */
  assetIdentifier: string | null;
  /** O que veio junto: carregador, capa, controle. */
  assetAccessories: string | null;
  /** Avarias pré-existentes: riscos, amassados, peça faltando. */
  assetCondition: string | null;
}

/** As cinco chaves, na ordem em que aparecem na tela e no PDF. */
export const ASSET_FIELDS = [
  {
    key: 'assetType',
    column: 'asset_type',
    label: 'Tipo de equipamento',
    placeholder: 'Ex.: notebook, ar-condicionado, geladeira',
  },
  {
    key: 'assetBrand',
    column: 'asset_brand',
    label: 'Marca e modelo',
    placeholder: 'Ex.: Samsung Galaxy S24, Split Carrier 12000 BTUs',
  },
  {
    key: 'assetIdentifier',
    column: 'asset_identifier',
    label: 'Nº de série / placa / IMEI',
    placeholder: 'O identificador único do aparelho',
  },
  {
    key: 'assetAccessories',
    column: 'asset_accessories',
    label: 'Acessórios recebidos',
    placeholder: 'Ex.: carregador, capa, controle remoto',
  },
  {
    key: 'assetCondition',
    column: 'asset_condition',
    label: 'Estado de conservação',
    placeholder: 'Riscos, amassados e avarias JÁ existentes na entrada',
  },
] as const satisfies readonly {
  key: keyof ServiceOrderAsset;
  column: string;
  label: string;
  placeholder: string;
}[];

/** Os nomes de coluna, para quem precisa iterar (executor offline). */
export const ASSET_COLUMNS = ASSET_FIELDS.map((f) => f.column) as readonly string[];

function normalize(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

/**
 * Monta as 5 colunas de uma vez — nenhum payload deve enumerá-las à mão, senão
 * um caminho esquece uma e o dado some sem erro.
 *
 * Aceita as duas formas (camelCase do formulário e snake_case do banco/fila)
 * porque os executores offline recebem a linha já em nome de coluna.
 */
export function toAssetColumns(
  // `unknown` de propósito: os chamadores são o formulário (camelCase), a fila
  // offline (snake_case) e a linha do banco, e nenhum tipo comum cobre os três
  // sem obrigar cada call-site a converter — que é justamente o trabalho que
  // este builder existe para tirar de lá.
  input: unknown,
): {
  asset_type: string | null;
  asset_brand: string | null;
  asset_identifier: string | null;
  asset_accessories: string | null;
  asset_condition: string | null;
} {
  const src = (input ?? {}) as Record<string, unknown>;
  return {
    asset_type: normalize(src.assetType ?? src.asset_type),
    asset_brand: normalize(src.assetBrand ?? src.asset_brand),
    asset_identifier: normalize(src.assetIdentifier ?? src.asset_identifier),
    asset_accessories: normalize(src.assetAccessories ?? src.asset_accessories),
    asset_condition: normalize(src.assetCondition ?? src.asset_condition),
  };
}

/** O leitor: extrai o bloco de uma linha vinda do servidor, do cache ou da fila. */
export function assetFromRow(row: Record<string, unknown> | null | undefined): ServiceOrderAsset {
  const columns = toAssetColumns(row ?? undefined);
  return {
    assetType: columns.asset_type,
    assetBrand: columns.asset_brand,
    assetIdentifier: columns.asset_identifier,
    assetAccessories: columns.asset_accessories,
    assetCondition: columns.asset_condition,
  };
}

/** Tem algo para mostrar? A tela e o PDF omitem o bloco inteiro quando não. */
export function hasAsset(value: ServiceOrderAsset | null | undefined): boolean {
  if (!value) return false;
  return ASSET_FIELDS.some((field) => Boolean(value[field.key]));
}

/** Só as linhas preenchidas, prontas para renderizar (tela ou PDF). */
export function assetEntries(
  value: ServiceOrderAsset | null | undefined,
): { key: keyof ServiceOrderAsset; label: string; text: string }[] {
  if (!value) return [];
  const out: { key: keyof ServiceOrderAsset; label: string; text: string }[] = [];
  for (const field of ASSET_FIELDS) {
    const text = value[field.key];
    if (text) out.push({ key: field.key, label: field.label, text });
  }
  return out;
}
